// src/services/websocket.js

import { WS_BASE_URL } from "./api";

// =====================================================
// PRODUCTION READY WEBSOCKET SERVICE
// =====================================================
//
// FEATURES
// - Singleton websocket manager
// - Auto reconnect
// - Event subscription system
// - Heartbeat ping
// - Connection state tracking
// - Safe cleanup
// - Realtime parking + layout updates
//
// EVENTS SUPPORTED
// - vehicle_entry
// - vehicle_exit
// - occupancy_update
// - slot_occupied
// - slot_vacant
// - slot_updated
// - layout_updated
// - connected
// - disconnected
//
// =====================================================

class ParkingWebSocket {

    constructor() {

        // =============================================
        // CORE SOCKET STATE
        // =============================================

        this.socket = null;

        this.connected = false;

        this.currentFacilityId = null;

        this.currentToken = null;

        // =============================================
        // RECONNECT STATE
        // =============================================

        this.reconnectAttempts = 0;

        this.maxReconnectAttempts = 15;

        this.reconnectDelay = 3000;

        this.reconnectTimeout = null;

        this.manualDisconnect = false;

        // =============================================
        // HEARTBEAT
        // =============================================

        this.heartbeatInterval = null;

        this.heartbeatMs = 30000;

        // =============================================
        // EVENT LISTENERS
        // =============================================

        this.listeners = {};

        // =============================================
        // CONNECTION LOCK
        // Prevent duplicate parallel connects
        // =============================================

        this.isConnecting = false;
    }

    // =================================================
    // CONNECT
    // =================================================

    connect(facilityId, token) {

        // =============================================
        // VALIDATION
        // =============================================

        if (!facilityId) {

            console.error(
                "WebSocket connect failed: facilityId missing"
            );

            return;
        }

        if (!token) {

            console.error(
                "WebSocket connect failed: token missing"
            );

            return;
        }

        // =============================================
        // PREVENT DUPLICATE CONNECTIONS
        // =============================================

        if (
            this.connected &&
            this.currentFacilityId === facilityId
        ) {

            console.log(
                "WebSocket already connected"
            );

            return;
        }

        if (this.isConnecting) {

            console.log(
                "WebSocket connection already in progress"
            );

            return;
        }

        // =============================================
        // RESET STATE
        // =============================================

        this.manualDisconnect = false;

        this.currentFacilityId = facilityId;

        this.currentToken = token;

        this.isConnecting = true;

        // =============================================
        // CLEANUP OLD SOCKET
        // =============================================

        this.cleanupSocket();

        // =============================================
        // CREATE WS URL
        // =============================================

        const url =

            `${WS_BASE_URL}/ws/facility/${facilityId}` +

            `?token=${token}`;

        console.log(
            "Connecting WebSocket:",
            url
        );

        // =============================================
        // CREATE SOCKET
        // =============================================

        this.socket = new WebSocket(url);

        // =============================================
        // ON OPEN
        // =============================================

        this.socket.onopen = () => {

            console.log(
                "✅ WebSocket Connected"
            );

            this.connected = true;

            this.isConnecting = false;

            this.reconnectAttempts = 0;

            this.startHeartbeat();

            this.emitLocal(
                "connected",
                {
                    facilityId
                }
            );
        };

        // =============================================
        // ON MESSAGE
        // =============================================

        this.socket.onmessage = (event) => {

            try {

                const data =
                    JSON.parse(event.data);

                // =====================================
                // DEBUG
                // =====================================

                console.log(
                    "📩 WS EVENT:",
                    data
                );

                // =====================================
                // HEARTBEAT RESPONSE
                // =====================================

                if (
                    data.type === "pong"
                ) {

                    return;
                }

                // =====================================
                // EMIT EVENT
                // =====================================

                if (data.event) {

                    this.emitLocal(
                        data.event,
                        data
                    );
                }

            } catch (error) {

                console.error(
                    "WebSocket parse error:",
                    error
                );
            }
        };

        // =============================================
        // ON CLOSE
        // =============================================

        this.socket.onclose = (event) => {

            console.warn(
                "❌ WebSocket Closed:",
                event.code,
                event.reason
            );

            this.connected = false;

            this.isConnecting = false;

            this.stopHeartbeat();

            this.emitLocal(
                "disconnected",
                {
                    code: event.code,
                    reason: event.reason
                }
            );

            // =========================================
            // AUTO RECONNECT
            // =========================================

            if (!this.manualDisconnect) {

                this.tryReconnect();
            }
        };

        // =============================================
        // ON ERROR
        // =============================================

        this.socket.onerror = (error) => {

            console.error(
                "WebSocket error:",
                error
            );
        };
    }

    // =================================================
    // DISCONNECT
    // =================================================

    disconnect() {

        this.manualDisconnect = true;

        this.connected = false;

        this.isConnecting = false;

        this.stopHeartbeat();

        // =============================================
        // STOP RECONNECT TIMER
        // =============================================

        if (this.reconnectTimeout) {

            clearTimeout(
                this.reconnectTimeout
            );

            this.reconnectTimeout = null;
        }

        // =============================================
        // CLOSE SOCKET
        // =============================================

        if (this.socket) {

            try {

                this.socket.close(1000);

            } catch (error) {

                console.error(
                    "Socket close error:",
                    error
                );
            }
        }

        this.socket = null;
    }

    // =================================================
    // CLEANUP SOCKET
    // =================================================

    cleanupSocket() {

        if (!this.socket) {
            return;
        }

        try {

            this.socket.onopen = null;
            this.socket.onmessage = null;
            this.socket.onclose = null;
            this.socket.onerror = null;

            this.socket.close();

        } catch (error) {

            console.error(
                "Socket cleanup error:",
                error
            );
        }

        this.socket = null;
    }

    // =================================================
    // RECONNECT
    // =================================================

    tryReconnect() {

        if (
            this.reconnectAttempts >=
            this.maxReconnectAttempts
        ) {

            console.error(
                "Max reconnect attempts reached"
            );

            return;
        }

        this.reconnectAttempts += 1;

        console.log(

            `🔄 Reconnect attempt ` +

            `${this.reconnectAttempts}/` +

            `${this.maxReconnectAttempts}`
        );

        this.reconnectTimeout = setTimeout(() => {

            this.connect(
                this.currentFacilityId,
                this.currentToken
            );

        }, this.reconnectDelay);
    }

    // =================================================
    // HEARTBEAT
    // =================================================

    startHeartbeat() {

        this.stopHeartbeat();

        this.heartbeatInterval =
            setInterval(() => {

                if (
                    this.socket &&
                    this.connected &&
                    this.socket.readyState ===
                    WebSocket.OPEN
                ) {

                    this.send({
                        type: "ping",
                        timestamp: Date.now()
                    });
                }

            }, this.heartbeatMs);
    }

    // =================================================
    // STOP HEARTBEAT
    // =================================================

    stopHeartbeat() {

        if (this.heartbeatInterval) {

            clearInterval(
                this.heartbeatInterval
            );

            this.heartbeatInterval = null;
        }
    }

    // =================================================
    // SEND MESSAGE
    // =================================================

    send(payload) {

        if (
            !this.socket ||
            !this.connected
        ) {

            console.warn(
                "Cannot send WS message: disconnected"
            );

            return false;
        }

        try {

            this.socket.send(
                JSON.stringify(payload)
            );

            return true;

        } catch (error) {

            console.error(
                "WebSocket send error:",
                error
            );

            return false;
        }
    }

    // =================================================
    // EVENT SUBSCRIBE
    // =================================================

    on(eventName, callback) {

        if (!eventName || !callback) {
            return;
        }

        if (!this.listeners[eventName]) {

            this.listeners[eventName] = [];
        }

        this.listeners[eventName].push(
            callback
        );
    }

    // =================================================
    // EVENT UNSUBSCRIBE
    // =================================================

    off(eventName, callback) {

        if (!this.listeners[eventName]) {
            return;
        }

        this.listeners[eventName] =
            this.listeners[eventName].filter(
                (cb) => cb !== callback
            );
    }

    // =================================================
    // REMOVE ALL LISTENERS
    // =================================================

    removeAllListeners() {

        this.listeners = {};
    }

    // =================================================
    // INTERNAL EVENT EMITTER
    // =================================================

    emitLocal(eventName, payload) {

        const callbacks =
            this.listeners[eventName];

        if (!callbacks?.length) {
            return;
        }

        callbacks.forEach((callback) => {

            try {

                callback(payload);

            } catch (error) {

                console.error(

                    `Listener error for event "${eventName}":`,

                    error
                );
            }
        });
    }

    // =================================================
    // CONNECTION STATUS
    // =================================================

    isConnected() {

        return (
            this.connected &&
            this.socket &&
            this.socket.readyState ===
            WebSocket.OPEN
        );
    }
}

// =====================================================
// SINGLETON INSTANCE
// =====================================================

const websocketService =
    new ParkingWebSocket();

export default websocketService;